import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAetherContext } from '#pplx/frontend/aether/components/AetherProvider/useAetherContext.js';
import { getOverlayZIndex } from '#pplx/frontend/aether/components/internal/elevation/elevation.js';
import { MenuProvider } from '#pplx/frontend/aether/components/internal/menu/MenuContext.js';
import { MenuOverlay } from '#pplx/frontend/aether/components/internal/menu/MenuOverlay.js';
import { SUBMENU_CONTENT_ANIMATION_CLASS_NAME } from '#pplx/frontend/aether/components/internal/menu/MenuSubmenuContentClassName.js';
import { useMenuFooterRegistry } from '#pplx/frontend/aether/components/internal/menu/useMenuFooterRegistry.js';
import { useMenuHeaderRegistry } from '#pplx/frontend/aether/components/internal/menu/useMenuHeaderRegistry.js';
import { useMenuSubmenuRegistration } from '#pplx/frontend/aether/components/internal/menu/useMenuSubmenuRegistry.js';
import { useElevation, useNearestSurface, } from '#pplx/frontend/aether/components/Surface/useSurface.js';
import { useHideWhenDetached } from '#pplx/frontend/aether/internal/floating-ui/useHideWhenDetached.js';
import { useCollisionPaddingWithTopInset } from '#pplx/frontend/aether/internal/popover-collision/useCollisionPaddingWithTopInset.js';
import { DropdownMenuPrimitive } from '#pplx/frontend/aether/primitives/DropdownMenuPrimitive/index.js';
import { useActivationOrderedOverlayStacking } from '#pplx/frontend/overlay-stacking/useOverlayStacking.js';
import { cloneElement, useCallback, useMemo, useState } from 'react';
const SIDE_OFFSET = 1;
const ALIGN_OFFSET = -4;
const SUBMENU_Z_INDEX_OFFSET = 1;
export function MenuSubmenu({ isOpen: controlledIsOpen, onToggle, triggerElement, children, disabled = false, minWidthPx, maxHeightPx = 300, maxWidthPx, openInstantly = false, }) {
    const collisionPadding = useCollisionPaddingWithTopInset(0);
    const shouldUseActivationOrder = useActivationOrderedOverlayStacking();
    const { isMobileStyle } = useAetherContext();
    const hideWhenDetached = useHideWhenDetached();
    const portalTargetElement = useNearestSurface();
    const parentElevation = useElevation();
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const { footer: mobileFooter, registry: mobileFooterRegistry } = useMenuFooterRegistry();
    const { header: mobileHeader, registry: mobileHeaderRegistry } = useMenuHeaderRegistry();
    // Use controlled state if provided, otherwise use internal state
    const isOpen = controlledIsOpen ?? internalIsOpen;
    const handleToggle = useCallback((newIsOpen) => {
        if (!disabled) {
            if (onToggle) {
                onToggle(newIsOpen);
            }
            else {
                setInternalIsOpen(newIsOpen);
            }
        }
    }, [disabled, onToggle]);
    // Create a close function that works with both controlled and uncontrolled state
    const closeSubmenu = useCallback(() => {
        if (onToggle) {
            onToggle(false);
        }
        else {
            setInternalIsOpen(false);
        }
    }, [onToggle]);
    // Register with the parent so scrolling can close all children and eager
    // hover-opening can close previously open siblings.
    const closeSiblingSubmenus = useMenuSubmenuRegistration(closeSubmenu);
    const handleMobileToggle = useCallback(() => {
        handleToggle(!isOpen);
    }, [handleToggle, isOpen]);
    // Radix hard-codes a ~100ms open delay in SubTrigger's onPointerMove. Opening
    // eagerly on pointer enter preempts that timer (its guard skips once the
    // submenu is already open), so the submenu appears with no perceptible delay.
    const handleTriggerPointerEnter = useCallback((event) => {
        if (event.pointerType === 'mouse' && !disabled) {
            // Eagerly opening on hover bypasses Radix's submenu intent timing. Close
            // any previously open sibling first so fast pointer movement cannot
            // leave multiple hover popovers stuck open.
            closeSiblingSubmenus();
            handleToggle(true);
        }
    }, [closeSiblingSubmenus, disabled, handleToggle]);
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            // Prevent default to avoid scrolling on Space key
            e.preventDefault();
            handleMobileToggle();
        }
    }, [handleMobileToggle]);
    const submenuZIndexStyle = useMemo(() => shouldUseActivationOrder
        ? undefined
        : { zIndex: getOverlayZIndex(parentElevation) + SUBMENU_Z_INDEX_OFFSET }, [parentElevation, shouldUseActivationOrder]);
    if (isMobileStyle) {
        // TODO: Use Radix Slot primitive instead of cloneElement.
        // Mobile: Render as expandable section within the bottom sheet
        // Can't use Radix Sub because there's no Radix Root in mobile mode (uses Dialog instead)
        const enhancedTrigger = cloneElement(triggerElement, {
            disabled: disabled,
            // Ensure clicks will toggle submenu expansion
            onClick: handleMobileToggle,
            // Ensure keyboard interaction also toggles submenu expansion
            // and prevents scroll
            onKeyDown: handleKeyDown,
            isExpanded: isOpen,
        });
        return (_jsxs("div", { children: [enhancedTrigger, isOpen && (_jsx(MenuProvider, { footerRegistry: mobileFooterRegistry, headerRegistry: mobileHeaderRegistry, children: _jsxs("div", { className: "border-soft ml-2 pl-4 mt-1 flex flex-col gap-px border-l-2", children: [mobileHeader, children, mobileFooter] }) }))] }));
    }
    // Desktop: Use Radix UI Sub (requires being inside Radix Root)
    return (_jsxs(DropdownMenuPrimitive.Sub, { open: isOpen, onOpenChange: handleToggle, children: [_jsx(DropdownMenuPrimitive.SubTrigger, { asChild: true, disabled: disabled, onPointerEnter: openInstantly ? handleTriggerPointerEnter : undefined, children: triggerElement }), _jsx(DropdownMenuPrimitive.Portal, { container: shouldUseActivationOrder ? undefined : portalTargetElement, children: _jsx(DropdownMenuPrimitive.SubContent, { sideOffset: SIDE_OFFSET, alignOffset: ALIGN_OFFSET, collisionPadding: collisionPadding, hideWhenDetached: hideWhenDetached, className: openInstantly ? undefined : SUBMENU_CONTENT_ANIMATION_CLASS_NAME, style: submenuZIndexStyle, children: _jsx(MenuOverlay, { minWidthPx: minWidthPx, maxHeightPx: maxHeightPx, maxWidthPx: maxWidthPx, children: children }) }) })] }));
}
//# sourceMappingURL=MenuSubmenu.js.map